import { NavLink } from "react-router-dom";
import { useAuth, ROLE_LABELS } from "@/contexts/AuthContext";
import { LOGO_URL } from "@/lib/api";
import {
  LayoutDashboard,
  Users,
  ArrowLeftRight,
  Wallet,
  FileText,
  Briefcase,
  IdCard,
  Shield,
  LogOut,
  CalendarDays,
  Target,
  Megaphone,
  Trophy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import AnnouncementBell from "@/components/AnnouncementBell";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/equipes", label: "Équipes", icon: Users, testid: "nav-equipes" },
  { to: "/calendrier", label: "Calendrier", icon: CalendarDays, testid: "nav-calendrier" },
  { to: "/classements", label: "Classements", icon: Trophy, testid: "nav-classements" },
  { to: "/communication", label: "Communication", icon: Megaphone, testid: "nav-communication",
    roles: ["admin", "president", "coach", "secretaire"] },
  { to: "/transferts", label: "Transferts", icon: ArrowLeftRight, testid: "nav-transferts" },
  { to: "/finances", label: "Finances", icon: Wallet, testid: "nav-finances", roles: ["admin", "president", "tresorier", "secretaire"] },
  { to: "/budget", label: "Budget prévisionnel", icon: Target, testid: "nav-budget", roles: ["admin", "president", "tresorier", "secretaire"] },
  { to: "/rh", label: "Personnel & RH", icon: Briefcase, testid: "nav-rh", roles: ["admin", "rh", "president", "secretaire"] },
  { to: "/membres", label: "Membres", icon: IdCard, testid: "nav-membres" },
  { to: "/documents", label: "Documents", icon: FileText, testid: "nav-documents" },
  { to: "/utilisateurs", label: "Utilisateurs", icon: Shield, testid: "nav-users", roles: ["admin", "president", "secretaire"] },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const role = user?.role || "joueur";
  const items = NAV_ITEMS.filter((i) => !i.roles || i.roles.includes(role) || role === "admin");

  return (
    <aside className="w-64 bg-white border-r border-slate-200 flex flex-col h-screen sticky top-0" data-testid="sidebar">
      <div className="px-5 py-5 border-b border-slate-200 flex items-center gap-3">
        <img src={LOGO_URL} alt="AMAL VBC" className="h-10 w-10 object-contain" />
        <div className="leading-tight flex-1">
          <div className="font-barlow font-bold text-lg tracking-tight">AMAL VBC</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Administration</div>
        </div>
        <AnnouncementBell />
      </div>

      <nav className="flex-1 overflow-y-auto py-4">
        <div className="px-4 mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
          Navigation
        </div>
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm font-medium border-l-[3px] transition-all ${
                  isActive
                    ? "bg-emerald-50 text-emerald-700 border-emerald-600"
                    : "text-slate-700 border-transparent hover:bg-slate-50"
                }`
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-slate-200 p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-9 w-9 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-sm">
            {user?.full_name?.[0]?.toUpperCase() || "?"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate" data-testid="user-fullname">{user?.full_name}</div>
            <div className="text-xs text-slate-500 truncate" data-testid="user-role">
              {ROLE_LABELS[user?.role] || user?.role}
            </div>
          </div>
        </div>
        <Button
          onClick={logout}
          data-testid="logout-button"
          variant="outline"
          className="w-full justify-start text-sm h-9 border-slate-200 hover:border-red-300 hover:text-red-600"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Se déconnecter
        </Button>
      </div>
    </aside>
  );
}
